import { Alert, Box, Collapse, Link, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useToasts } from 'react-toast-notifications';
import { getFlagStateValue } from '../../../experimentation/base';
import { useExperimentation } from '../../../experimentation/client';
import {
  useGetCarriersQuery,
  useGetPlansLazyQuery,
} from '../../../generated/graphql';
import { useAppDispatch } from '../../../pages/assessment';
import { updateInsuranceForm } from '../../../redux/assessment';
import { selectLdFlags } from '../../../redux/ld-flags';
import { rules } from '../demographics/rules';
import { AssessmentButtons, AssessmentCard } from '../shared';
import { ControlledAutocomplete } from '../shared/controlled-autocomplete';
import { DialogBox } from '../shared/dialog-box';
import {
  Carrier,
  Plan,
  insuranceRequirementHelper,
  otherPlan,
  visitCostHelper,
} from './insurance';
import InsuranceLogoGrid from './insurance/insurance-logo-grid';
import InsuranceUsageInfoModal from './insurance/insurance-usage-info-modal';

type FormValues = {
  carrier: Carrier;
  plan?: Plan;
};

const InsuranceForm = () => {
  const dispatch = useAppDispatch();
  const { addToast } = useToasts();
  const { shouldShowInsuranceDialogBoxes } = useSelector(selectLdFlags);

  const [assessmentInsuranceScreeIconsAndModal] = useExperimentation({
    flagName: 'assessment-insurance-screen-w-icons-and-modal',
    variables: ['assessment-insurance-screen-w-icons-and-modal'],
  });
  const assessmentInsuranceScreenIconsAndModalValue = getFlagStateValue(
    assessmentInsuranceScreeIconsAndModal,
    'assessment-insurance-screen-w-icons-and-modal'
  );

  const [shouldShowPlan, setShouldShowPlan] = useState(true);
  const [isInsuranceInfoModalOpen, setIsInsuranceInfoModalOpen] =
    useState(false);

  const { data: carriersData, loading: carriersLoading } =
    useGetCarriersQuery();
  const allCarriers = carriersData?.getCarriers;
  const carriers: Carrier[] = allCarriers
    ?.map(({ name }) => ({
      label: name.toLowerCase() === 'other' ? "I don't see my insurer" : name,
      value: name,
    }))
    .sort((a, b) =>
      a.value.toLowerCase() === 'other'
        ? -1
        : b.value.toLowerCase() === 'other'
        ? 1
        : 0
    );

  const [getPlans, { data: plansData, loading: plansLoading }] =
    useGetPlansLazyQuery();
  const allPlans = plansData?.getPlans;
  const plans: Plan[] = allPlans?.map(it => ({
    label: it.plan,
    value: `${it.plan}_${it.inNetwork ? 'in-network' : 'out-of-network'}`,
    inNetwork: it.inNetwork,
    isOutofStateNeeded: it.isOutOfStateNeeded,
    isReferralNeeded: it.isReferralNeeded,
    notes: it.notes,
  }));
  plans?.unshift(otherPlan);

  const {
    control,
    handleSubmit,
    formState: { isValid, isSubmitting },
    watch,
    setValue,
    trigger,
  } = useForm<FormValues>({
    mode: 'all',
  });

  const selectedCarrier = watch('carrier');

  useEffect(() => {
    if (selectedCarrier) {
      getPlans({
        variables: {
          carrier: selectedCarrier.value,
        },
      });
    }

    setValue('plan', null);

    const fullProviderInfo = allCarriers?.find(
      ({ name }) => name === selectedCarrier?.value
    );

    setShouldShowPlan(fullProviderInfo?.isInNetwork);
  }, [selectedCarrier]);

  const onSubmit: SubmitHandler<FormValues> = async data => {
    const providerInput = {
      questionCode: 'MI_InsuranceProvider',
      answerValue: [data.carrier?.value],
    };

    const planInput = {
      questionCode: 'MI_InsurancePlan',
      answerValue: [data.plan?.value],
    };

    const insuranceInput = shouldShowPlan
      ? [providerInput, planInput]
      : [providerInput];

    try {
      await dispatch(updateInsuranceForm(insuranceInput, !shouldShowPlan));
    } catch (e) {
      addToast(
        'Sorry, something went wrong. Please refresh the page and try again.',
        { appearance: 'error' }
      );
    }
  };

  const filterInsuranceOptions = (options, { inputValue }) =>
    // Always include "I can't find my plan" if it exists
    options.filter(({ value }) => {
      const matchesInput = value
        .toLowerCase()
        .includes(inputValue.toLowerCase());
      return matchesInput || value === 'Other';
    });

  const InsurancePlanAutocomplete = (
    <ControlledAutocomplete<FormValues>
      name="plan"
      control={control}
      rules={rules.insurancePlan}
      disabled={!selectedCarrier}
      loading={plansLoading}
      options={plans || []}
      filterOptions={filterInsuranceOptions}
      getOptionLabel={option => option.label ?? ''}
      isOptionEqualToValue={(opt, val) => opt.value === val.value}
      label="Which plan do you have?"
      sx={{ mt: 2 }}
    />
  );
  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {assessmentInsuranceScreenIconsAndModalValue ? (
        <>
          <InsuranceUsageInfoModal
            isOpen={isInsuranceInfoModalOpen}
            setIsOpen={setIsInsuranceInfoModalOpen}
          />
          <InsuranceLogoGrid />
        </>
      ) : (
        shouldShowInsuranceDialogBoxes && (
          <>
            <DialogBox
              title="Do I have to use insurance?"
              content={insuranceRequirementHelper()}
            />
            <DialogBox
              title="How much will my visit cost?"
              content={visitCostHelper()}
            />
          </>
        )
      )}
      <AssessmentCard>
        {assessmentInsuranceScreenIconsAndModalValue && (
          <Typography variant="h6" sx={{ mb: 2 }}>
            See if we take yours
          </Typography>
        )}
        <ControlledAutocomplete<FormValues>
          name="carrier"
          control={control}
          rules={rules.insuranceCarrier}
          loading={carriersLoading}
          options={carriers || []}
          filterOptions={filterInsuranceOptions}
          getOptionLabel={option => option.label ?? ''}
          isOptionEqualToValue={(opt, val) => opt.value === val.value}
          label="Who's your insurer?"
          sx={{ mt: 2 }}
        />
        {assessmentInsuranceScreenIconsAndModalValue
          ? InsurancePlanAutocomplete
          : selectedCarrier && (
              <Collapse
                in={shouldShowPlan}
                unmountOnExit
                onExited={async () => trigger('carrier')}
              >
                {InsurancePlanAutocomplete}
              </Collapse>
            )}
        <Collapse
          in={
            selectedCarrier?.value.includes('Blue Cross') ||
            selectedCarrier?.value.includes('Blue Shield')
          }
          unmountOnExit
        >
          <Alert severity="info" sx={{ mt: 3 }}>
            <Typography variant="body2" sx={{ mb: 2 }}>
              If you're a Blue Cross Blue Shield member, enter the first 3
              characters of your member ID. You can also use your plan name if
              you know it.
            </Typography>
            <Box
              component="img"
              alt="Example BCBS insurance card"
              src="/bcbs-insurance-card.png"
              sx={{ borderRadius: 1, maxWidth: '100%', height: 'auto' }}
            />
          </Alert>
        </Collapse>
      </AssessmentCard>
      {assessmentInsuranceScreenIconsAndModalValue && (
        <Box sx={{ margin: 3 }}>
          <Link
            sx={{ cursor: 'pointer' }}
            onClick={() => setIsInsuranceInfoModalOpen(true)}
          >
            Do I have to use insurance?
          </Link>
        </Box>
      )}
      <AssessmentButtons
        primary={{
          children: 'Continue',
          type: 'submit',
          disabled: !isValid || isSubmitting,
          loading: isSubmitting,
        }}
      />
    </form>
  );
};

export default InsuranceForm;
